<template>
  <div class="scada-dashboard">
    <div class="dashboard-container">
      <!-- 产线列表视图 -->
      <production-line-list
        v-if="viewMode === 'list'"
        :production-lines="mockProductionLines"
        :loading="false"
        :error="null"
        :user-name="userName"
        @select-line="selectProductionLine"
      />
      
      <!-- 产线详情视图 -->
      <template v-else-if="viewMode === 'detail'">
        <!-- 顶部栏 -->
        <top-bar :user-name="dashboardData.userName" class="load-animation" />
        
        <!-- 信息栏 -->
        <info-bar 
          v-if="dashboardData.workOrder" 
          :work-order="dashboardData.workOrder" 
          class="load-animation delay-1"
        >
          <template #extra>
            <button @click="backToList" class="back-button">
              <i class="el-icon-back"></i>
              返回产线列表
            </button>
          </template>
        </info-bar>
        
        <!-- 主内容区 -->
        <div class="main-content load-animation delay-2">
        <!-- 左侧区域 -->
        <div class="left-section">
          <!-- 上方：生产进度 + 设备运行状态 -->
          <div class="top-section">
            <progress-module 
              v-if="dashboardData.workOrder"
              :plan-quantity="dashboardData.workOrder.planQuantity"
              :collected-quantity="dashboardData.workOrder.collectedQuantity"
              :plan-unit="dashboardData.workOrder.planUnit"
              :plan-unit-name="dashboardData.workOrder.planUnitName"
              class="progress-wrapper"
            />
            <gauge-module 
              v-if="dashboardData.machines && dashboardData.machines.length > 0"
              :machines="dashboardData.machines"
              class="gauge-wrapper"
            />
          </div>
          
          <!-- 下方：工控机卡片 -->
          <div class="bottom-section">
            <machine-card
              v-for="machine in dashboardData.machines"
              :key="machine.machineCode"
              :machine="machine"
            />
          </div>
        </div>
        
        <!-- 右侧：预警信息 -->
        <div class="right-section">
          <alarm-module 
            v-if="dashboardData.machines"
            :machines="dashboardData.machines" 
          />
        </div>
      </div>
      
      <!-- 加载状态 -->
      <div v-if="loading && !dashboardData.workOrder" class="loading-overlay">
        <div class="loading-spinner"></div>
        <div class="loading-text">数据加载中...</div>
      </div>
      
        <!-- 错误提示 -->
        <div v-if="error" class="error-overlay">
          <div class="error-icon">⚠</div>
          <div class="error-message">{{ error }}</div>
          <div class="error-actions">
            <button @click="loadMockData" class="retry-button">重试</button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import TopBar from './components/TopBar.vue'
import InfoBar from './components/InfoBar.vue'
import ProgressModule from './components/ProgressModule.vue'
import GaugeModule from './components/GaugeModule.vue'
import MachineCard from './components/MachineCard.vue'
import AlarmModule from './components/AlarmModule.vue'
import ProductionLineList from './components/ProductionLineList.vue'

export default {
  name: 'ScadaDashboard',
  components: {
    TopBar,
    InfoBar,
    ProgressModule,
    GaugeModule,
    MachineCard,
    AlarmModule,
    ProductionLineList
  },
  data() {
    return {
      viewMode: 'list', // 'list' 或 'detail'
      selectedWorklineCode: '', // 选中的产线号
      mockProductionLines: [], // Mock产线列表数据
      userName: '测试用户', // 用户名
      dashboardData: {
        userName: '测试用户',
        workOrder: null,
        machines: []
      },
      loading: false,
      error: null,
      timer: null,
      resizeTimer: null
    }
  },
  mounted() {
    // 初始化Mock产线列表数据
    this.initMockProductionLines()
    
    // 从路由参数获取产线号
    const worklineCode = this.$route.query.worklineCode
    
    if (worklineCode) {
      // 如果有产线号，直接进入详情页
      this.selectedWorklineCode = worklineCode
      this.viewMode = 'detail'
      this.loadMockData()
    } else {
      // 否则显示产线列表
      this.viewMode = 'list'
    }
    
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
  },
  methods: {
    // 初始化Mock产线列表数据
    initMockProductionLines() {
      this.mockProductionLines = [
        {
          worklineCode: 'LINE001',
          worklineName: '产线1',
          workOrderNo: 'WO202604070001',
          progressPercentage: 57.7,
          collectedQuantity: 8650,
          planQuantity: 15000,
          lastUploadTime: new Date().toISOString(),
          alarmCount: 0
        },
        {
          worklineCode: 'LINE002',
          worklineName: '产线2',
          workOrderNo: 'WO202604070002',
          progressPercentage: 68.5,
          collectedQuantity: 6850,
          planQuantity: 10000,
          lastUploadTime: new Date(Date.now() - 10000).toISOString(),
          alarmCount: 2
        },
        {
          worklineCode: 'LINE003',
          worklineName: '产线3',
          workOrderNo: 'WO202604070003',
          progressPercentage: 42.3,
          collectedQuantity: 4230,
          planQuantity: 10000,
          lastUploadTime: new Date(Date.now() - 25000).toISOString(),
          alarmCount: 1
        }
      ]
    },
    
    // 选择产线，进入详情页
    selectProductionLine(worklineCode) {
      this.selectedWorklineCode = worklineCode
      this.viewMode = 'detail'
      
      // 更新路由参数
      this.$router.push({
        query: { worklineCode }
      })
      
      // 加载对应产线的详情数据
      this.loadMockData()
    },
    
    // 返回产线列表
    backToList() {
      this.viewMode = 'list'
      this.selectedWorklineCode = ''
      
      // 清除路由参数
      this.$router.push({
        query: {}
      })
    },
    
    loadMockData() {
      // 模拟数据 - 3个工控机的实际生产场景
      this.dashboardData = {
        userName: '测试用户',
        workOrder: {
          workOrderNo: 'WO202604070001',
          productionBatch: 'BATCH-001',
          productName: '测试产品A',
          factoryName: '第一工厂',
          workshopName: '生产车间1',
          productionLineName: '产线1',
          planDate: '2026-04-07',
          planQuantity: 15000,
          planUnit: '箱',
          planUnitName: '箱', // 添加计划单位名称
          collectedQuantity: 8650
        },
        machines: [
          {
            machineCode: 'MC001',
            machineName: '工控机1号',
            machineStatus: '1', // 1=运行
            associationLevel: 1,
            associationLevelName: '盒箱关联', // 添加关联级别名称
            parentCollectCount: 3200,
            childCollectCount: 0,
            associationRate: 95.5, // ≥90% 绿色边框
            alarmCount: 0,
            alarms: [],
            cameras: [
              {
                cameraName: '相机1号',
                cameraStatus: '1', // 1=在线
                collectCount: 3200,
                failCount: 150, // 95.5% = 3200/(3200+150)
                collectRate: 95.5  // ≥90% 绿色
              },
              {
                cameraName: '相机2号',
                cameraStatus: '1', // 1=在线
                collectCount: 3200,
                failCount: 267, // 92.3% = 3200/(3200+267)
                collectRate: 92.3  // ≥90% 绿色
              }
            ]
          },
          {
            machineCode: 'MC002',
            machineName: '工控机2号',
            machineStatus: '2', // 2=停止
            associationLevel: 2,
            associationLevelName: '箱托关联', // 添加关联级别名称
            parentCollectCount: 0,
            childCollectCount: 2850,
            associationRate: 85.2, // 80-89% 黄色边框
            alarmCount: 10,
            alarms: [
              {
                alarmTime: '2026-04-07T15:20:00',
                alarmContent: '相机3号采集失败率偏高',
                uploadTime: '2026-04-07T15:20:05'
              },
              {
                alarmTime: '2026-04-07T15:15:00',
                alarmContent: '网络延迟超过阈值',
                uploadTime: '2026-04-07T15:15:03'
              },
              {
                alarmTime: '2026-04-07T15:10:00',
                alarmContent: '数据传输异常',
                uploadTime: '2026-04-07T15:10:02'
              },
              {
                alarmTime: '2026-04-07T15:05:00',
                alarmContent: 'CPU使用率过高',
                uploadTime: '2026-04-07T15:05:01'
              },
              {
                alarmTime: '2026-04-07T15:00:00',
                alarmContent: '内存使用率达到80%',
                uploadTime: '2026-04-07T15:00:04'
              },
              {
                alarmTime: '2026-04-07T14:55:00',
                alarmContent: '磁盘IO异常',
                uploadTime: '2026-04-07T14:55:02'
              },
              {
                alarmTime: '2026-04-07T14:50:00',
                alarmContent: '相机4号响应超时',
                uploadTime: '2026-04-07T14:50:06'
              },
              {
                alarmTime: '2026-04-07T14:45:00',
                alarmContent: '数据库连接池不足',
                uploadTime: '2026-04-07T14:45:03'
              },
              {
                alarmTime: '2026-04-07T14:40:00',
                alarmContent: '温度传感器读数异常',
                uploadTime: '2026-04-07T14:40:05'
              },
              {
                alarmTime: '2026-04-07T14:35:00',
                alarmContent: '系统日志文件过大',
                uploadTime: '2026-04-07T14:35:01'
              }
            ],
            cameras: [
              {
                cameraName: '相机3号',
                cameraStatus: '1', // 1=在线
                collectCount: 2850,
                failCount: 445, // 86.5% = 2850/(2850+445)
                collectRate: 86.5  // 80-89% 黄色
              },
              {
                cameraName: '相机4号',
                cameraStatus: '1', // 1=在线
                collectCount: 2850,
                failCount: 575, // 83.2% = 2850/(2850+575)
                collectRate: 83.2  // 80-89% 黄色
              }
            ]
          },
          {
            machineCode: 'MC003',
            machineName: '工控机3号',
            machineStatus: '3', // 3=未启动
            associationLevel: 2,
            associationLevelName: '箱托关联', // 添加关联级别名称
            parentCollectCount: 0,
            childCollectCount: 2600,
            associationRate: 72.8, // <80% 红色边框
            alarmCount: 3,
            alarms: [
              {
                alarmTime: '2026-04-07T13:30:00',
                alarmContent: '存储空间使用率达到85%',
                uploadTime: '2026-04-07T13:30:08'
              },
              {
                alarmTime: '2026-04-07T13:15:00',
                alarmContent: '相机6号离线',
                uploadTime: '2026-04-07T13:15:02'
              },
              {
                alarmTime: '2026-04-07T13:00:00',
                alarmContent: '温度传感器异常',
                uploadTime: '2026-04-07T13:00:05'
              }
            ],
            cameras: [
              {
                cameraName: '相机5号',
                cameraStatus: '1', // 1=在线
                collectCount: 2600,
                failCount: 971, // 72.8% = 2600/(2600+971)
                collectRate: 72.8  // <80% 红色
              },
              {
                cameraName: '相机6号',
                cameraStatus: '2', // 2=离线
                collectCount: 2600,
                failCount: 1195, // 68.5% = 2600/(2600+1195)
                collectRate: 68.5  // <80% 红色
              }
            ]
          }
        ]
      }
    },
    
    fetchData() {
      // 模拟数据刷新
      this.loadMockData()
    },
    
    handleResize() {
      // 防抖处理resize事件
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      
      this.resizeTimer = setTimeout(() => {
        // 触发图表重绘
        this.$nextTick(() => {
          window.dispatchEvent(new Event('resize'))
        })
      }, 300)
    }
  }
}
</script>

<style lang="scss" scoped>
.scada-dashboard {
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%);
  overflow: hidden;
  position: relative;
  
  // 科技风格背景网格
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(rgba(0, 255, 255, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 255, 255, 0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    pointer-events: none;
  }
}

.dashboard-container {
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  display: flex;
  gap: 20px;
  margin-top: 20px;
  overflow: hidden;
}

.left-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
}

.top-section {
  height: 35%;
  min-height: 280px;
  display: flex;
  gap: 20px;
  flex-shrink: 0;
}

.progress-wrapper {
  width: 30%;
  min-width: 280px;
}

.gauge-wrapper {
  flex: 1;
  min-width: 400px;
}

.bottom-section {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  overflow-y: auto;
  padding-right: 10px;
  padding-top: 10px;
  
  // 自定义滚动条
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 255, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(0, 255, 255, 0.5);
    }
  }
}

.right-section {
  width: 20%;
  min-width: 260px;
  flex-shrink: 0;
}

// 加载状态
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 14, 39, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(0, 255, 255, 0.1);
  border-top-color: #00ffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 20px;
  color: #00ffff;
  font-size: 16px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(20, 10, 10, 0.95);
  border: 2px solid rgba(255, 0, 0, 0.5);
  border-radius: 8px;
  padding: 40px;
  z-index: 1000;
  text-align: center;
  min-width: 400px;
  box-shadow: 0 0 30px rgba(255, 0, 0, 0.3);
}

.error-icon {
  font-size: 48px;
  color: #ff4444;
  margin-bottom: 20px;
  animation: pulse 2s ease-in-out infinite;
}

.error-message {
  color: #ff4444;
  font-size: 16px;
  margin-bottom: 20px;
  text-shadow: 0 0 10px rgba(255, 68, 68, 0.5);
  line-height: 1.6;
}

.error-actions {
  margin-top: 20px;
}

.retry-button {
  background: linear-gradient(135deg, rgba(255, 0, 0, 0.2), rgba(255, 0, 0, 0.1));
  border: 1px solid rgba(255, 0, 0, 0.5);
  color: #ff4444;
  padding: 10px 30px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 0, 0, 0.3), rgba(255, 0, 0, 0.2));
    box-shadow: 0 0 15px rgba(255, 0, 0, 0.5);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
}

// 页面加载动画
@keyframes pageLoadSequence {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

.load-animation {
  animation: pageLoadSequence 0.6s ease-out forwards;
  opacity: 0;
  
  &.delay-1 {
    animation-delay: 0.1s;
  }
  
  &.delay-2 {
    animation-delay: 0.2s;
  }
  
  &.delay-3 {
    animation-delay: 0.3s;
  }
}

// 返回按钮样式
.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(0, 255, 255, 0.1));
  border: 1px solid rgba(0, 255, 255, 0.5);
  color: #00ffff;
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  box-shadow: 0 0 10px rgba(0, 255, 255, 0.3);
  
  i {
    font-size: 16px;
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(0, 255, 255, 0.3), rgba(0, 255, 255, 0.2));
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
}
</style>
